import { MenuSectionType } from "../../types/menu-tree.type";
import { Role } from "./role.model";
import { APP_PATHS } from "./app-route-paths";
import { MERCHANT_DASHBOARD_ROLES } from "./merchant-dashboard-roles";

/**
 * Rail order:
 * Onboarding → Tableau de bord → Messagerie → Catalog → Workflow → Diffusion →
 * Payment → Reports → Paramètres → Platform
 */
export const MenuSections: MenuSectionType[] = [
  // ==================== ONBOARDING (temporary) ====================
  // Its own collection on the rail, on purpose: the whole section is meant to be
  // deleted once onboarding is finished rolling out — remove this entry and the
  // /onboarding route in app.routes.ts and nothing else has to change.
  {
    section: "onboarding",
    label: "MENU.SECTION.ONBOARDING",
    icon: "onboarding",
    directLink: APP_PATHS.onboarding,
    // Same audience as the header setup pill: the groups whose users have at
    // least one checklist step (HEADER_SETUP_STEPS / HEADER_OPTIONAL_STEPS).
    groups: ["/ADMINISTRATION", "/ORDER", "/MESSAGING"],
    items: [
      {
        menu: "onboarding-checklist",
        path: APP_PATHS.onboarding,
        label: "MENU.SECTION.ONBOARDING",
        icon: "onboarding",
        groups: ["/ADMINISTRATION", "/ORDER", "/MESSAGING"],
      },
    ],
  },

  // ==================== TABLEAU DE BORD ====================
  {
    section: "dashboard",
    label: "MENU.SECTION.DASHBOARD",
    icon: "layout-dashboard",
    // Rail click goes straight to the dashboard (the previous one stays reachable at /dashboard-v0, no menu entry).
    directLink: APP_PATHS.dashboard,
    items: [
      {
        menu: "dashboard",
        path: APP_PATHS.dashboard,
        label: "MENU.SECTION.DASHBOARD",
        icon: "layout-dashboard",
        // No group gate: any role of a dashboard section opens it (same as the route).
        authorities: [...MERCHANT_DASHBOARD_ROLES],
      },
    ],
  },

  // ==================== MESSAGERIE ====================
  {
    section: "messaging",
    label: "MENU.SECTION.MESSAGING",
    icon: "inbox",
    // /ORDER included so Contacts (below) reaches catalog-only merchants too —
    // team-inbox and tickets still gate on their own narrower item-level groups.
    groups: ["/MESSAGING", "/SUPPORT", "/ORDER"],
    items: [
      {
        menu: "team-inbox",
        path: APP_PATHS.messaging.inbox,
        label: "MENU.TEAM_INBOX",
        icon: "inbox",
        groups: ["/MESSAGING"],
        authorities: [Role.TEAM_INBOX, Role.CONVERSATION_VIEW],
        badge: "unreadConversations",
      },
      {
        menu: "contact",
        path: APP_PATHS.messaging.contacts,
        label: "MENU.CONTACTS",
        icon: "address-book",
        // Contacts belong to WhatsApp merchants AND catalog/e-commerce merchants —
        // a merchant's customers are contacts either way.
        groups: ["/MESSAGING", "/ORDER"],
        authorities: [Role.MANAGE_CONTACT, Role.CONTACT_VIEW],
        items: [
          {
            path: APP_PATHS.messaging.contacts,
            label: "MENU.CONTACT_MANAGEMENT",
            icon: "address-book",
            authorities: [Role.MANAGE_CONTACT, Role.CONTACT_VIEW],
          },
          {
            path: APP_PATHS.messaging.lists,
            label: "MENU.LISTS_AND_SEGMENTS",
            icon: "list",
            alsoActiveOn: [APP_PATHS.messaging.lists, APP_PATHS.messaging.segments],
            groups: ["/MESSAGING"],
            authorities: [Role.MANAGE_CONTACT, Role.CONTACT_VIEW],
          },
        ],
      },
      {
        path: APP_PATHS.messaging.tickets,
        label: "MENU.TICKETS",
        icon: "ticket",
        groups: ["/SUPPORT"],
        authorities: [Role.MANAGE_TICKET, Role.TICKET_VIEW],
        badge: "openTickets",
      },
    ],
  },

  // ==================== CATALOG ====================
  {
    section: "catalog",
    label: "MENU.SECTION.CATALOG",
    icon: "store",
    groups: ["/ORDER", "/VOUCHER", "/DELIVERY"],
    defaultMenu: "order",
    items: [
      {
        menu: "whatsapp-storefront",
        path: APP_PATHS.catalog.whatsappStorefront,
        label: "MENU.WHATSAPP_STOREFRONT",
        icon: "store",
        groups: ["/ORDER"],
        authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
      },
      {
        menu: "voucher",
        path: APP_PATHS.catalog.vouchers,
        label: "MENU.VOUCHER.TITLE",
        icon: "receipt",
        hidden: true,
        groups: ["/VOUCHER"],
        authorities: [Role.MANAGE_VOUCHER, Role.VOUCHER_VIEW],
      },
      {
        menu: "order",
        path: APP_PATHS.catalog.orders,
        label: "MENU.ORDERS",
        icon: "shopping-cart",
        groups: ["/ORDER"],
        authorities: [Role.ORDERS, Role.ORDERS_VIEW, Role.REFERENTIAL_MANAGE],
        hideForActivity: "RESERVATION_HOSTELS",
      },
      {
        menu: "reservation",
        path: APP_PATHS.catalog.reservations,
        label: "MENU.RESERVATION.TITLE",
        icon: "calendar-check",
        groups: ["/ORDER"],
        activityCondition: "RESERVATION_HOSTELS",
      },
      {
        menu: "reservation-categories",
        path: APP_PATHS.catalog.categories,
        label: "MENU.RESERVATION.CATEGORIES",
        icon: "layers",
        groups: ["/ORDER"],
        activityCondition: "RESERVATION_HOSTELS",
      },
      {
        menu: "reservation-rooms",
        path: APP_PATHS.catalog.products,
        label: "MENU.RESERVATION.ROOMS",
        icon: "bed",
        groups: ["/ORDER"],
        activityCondition: "RESERVATION_HOSTELS",
        queryParams: { itemType: "ROOM" },
      },
      {
        menu: "reservation-activities",
        path: APP_PATHS.catalog.products,
        label: "MENU.RESERVATION.ACTIVITIES",
        icon: "bell",
        groups: ["/ORDER"],
        activityCondition: "RESERVATION_HOSTELS",
        queryParams: { itemType: "EXTRA" },
      },
      {
        menu: "product",
        path: APP_PATHS.catalog.products,
        label: "MENU.PRODUCTS",
        icon: "package",
        groups: ["/ORDER"],
        authorities: [Role.PRODUCT, Role.PRODUCT_VIEW],
        hideForActivity: "RESERVATION_HOSTELS",
        items: [
          {
            path: APP_PATHS.catalog.products,
            label: "MENU.PRODUCT_LIST",
            icon: "list",
            authorities: [Role.PRODUCT, Role.PRODUCT_VIEW],
          },
          {
            path: APP_PATHS.catalog.categories,
            label: "CATEGORY.TITLE",
            icon: "layers",
            authorities: [Role.PRODUCT, Role.PRODUCT_VIEW],
          },
          {
            path: APP_PATHS.catalog.discountCodes,
            label: "MENU.DISCOUNT_CODES",
            icon: "ticket",
            authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
            // A restaurant has "Promotions" on its own line (below).
            hideForActivity: "RESTAURANT",
          },
        ],
      },
      // Restaurant: Vitrine WhatsApp, Commandes, Menu (Plats, Rubriques), Tables, Points de vente, Promotions.
      // Delivery is a side panel of the Commandes page (?panel=delivery), not a menu line.
      {
        // One QR code per table: only restaurants serve at tables.
        menu: "dining-tables",
        path: APP_PATHS.catalog.diningTables,
        label: "MENU.DINING_TABLES",
        icon: "qr-code",
        groups: ["/ORDER"],
        authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
        activityCondition: "RESTAURANT",
      },
      {
        menu: "restaurant-sale-points",
        path: APP_PATHS.catalog.whatsappStorefront,
        queryParams: { tab: "salePoints" },
        label: "MENU.RESTAURANT.SALE_POINTS",
        icon: "map-pin",
        groups: ["/ORDER"],
        authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
        activityCondition: "RESTAURANT",
      },
      {
        menu: "restaurant-promotions",
        path: APP_PATHS.catalog.discountCodes,
        label: "MENU.RESTAURANT.PROMOTIONS",
        icon: "ticket",
        groups: ["/ORDER"],
        authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
        activityCondition: "RESTAURANT",
      },
      {
        menu: "delivery",
        path: APP_PATHS.catalog.delivery,
        label: "MENU.DELIVERY.TITLE",
        icon: "truck",
        hidden: true,
        groups: ["/DELIVERY"],
        authorities: [Role.MERCHANT, Role.MANAGE_DELIVERYMAN, Role.PARCELS],
        hideForActivity: "RESERVATION_HOSTELS",
      },
    ],
  },

  // ==================== WORKFLOW ====================
  {
    section: "workflow",
    label: "MENU.SECTION.WORKFLOW",
    icon: "workflow",
    groups: ["/MESSAGING", "/SOLUTION"],
    items: [
      {
        menu: "templates",
        path: APP_PATHS.workflow.templates,
        label: "MENU.TEMPLATES",
        icon: "clipboard",
        authorities: [
          Role.MANAGE_TEMPLATE,
          Role.REFERENTIAL_MANAGE,
          Role.MANAGE_FLOW,
          Role.WORKFLOW_VIEW,
        ],
      },
      {
        menu: "automations",
        // Workflows first: it is where most merchants start.
        path: APP_PATHS.workflow.bots,
        label: "MENU.AUTOMATIONS.TITLE",
        icon: "workflow",
        authorities: [
          Role.AUTOMATION_DEF_ACTIONS,
          Role.AUTOMATION,
          Role.MANAGE_VOUCHER,
          Role.VOUCHER_VIEW,
          Role.REFERENTIAL_MANAGE,
          Role.WORKFLOW_VIEW,
          Role.ADMIN_SOLUTION_UI,
        ],
        roleMode: "any",
        items: [
          {
            path: APP_PATHS.workflow.bots,
            label: "MENU.AUTOMATIONS.WORKFLOW",
            icon: "workflow",
            authorities: [Role.MANAGE_BOTS, Role.MANAGE_FLOW, Role.WORKFLOW_VIEW, Role.ADMIN_SOLUTION_UI],
            roleMode: "any",
          },
          {
            path: APP_PATHS.workflow.channelMessages,
            label: "MENU.AUTOMATIONS.CHANNEL",
            icon: "share-2",
            authorities: [Role.AUTOMATION, Role.REFERENTIAL_MANAGE, Role.WORKFLOW_VIEW],
          },
          {
            path: APP_PATHS.workflow.pendingMessages,
            label: "MENU.AUTOMATIONS.PENDING_MESSAGE",
            icon: "clock",
            authorities: [Role.MANAGE_VOUCHER, Role.VOUCHER_VIEW],
          },
        ],
      },
      {
        menu: "ai-agents",
        path: APP_PATHS.workflow.aiAgents,
        label: "MENU.SECTION.AI_AGENTS",
        icon: "user-cog",
        authorities: [Role.MANAGE_AIASSISTANT, Role.WORKFLOW_VIEW],
      },
    ],
  },

  // ==================== DIFFUSION (BROADCAST) ====================
  {
    section: "broadcast",
    label: "MENU.CAMPAIGNS",
    icon: "megaphone",
    groups: ["/MESSAGING"],
    // One entry only: the rail click opens the campaigns page, no sub-menu panel.
    directLink: APP_PATHS.campaigns.history,
    items: [
      {
        menu: "broadcast-history",
        path: APP_PATHS.campaigns.history,
        label: "MENU.CAMPAIGNS_HISTORY",
        icon: "pie-chart",
        authorities: [Role.BROADCAST_HISTORY, Role.BROADCAST_VIEW],
      },
    ],
  },

  // ==================== PAYMENT ====================
  {
    section: "payment",
    label: "MENU.SECTION.PAYMENT",
    icon: "credit-card",
    groups: ["/PAYMENT"],
    items: [
      {
        menu: "payment-links",
        path: APP_PATHS.payment.links,
        label: "PAYMENT_URL.PAGE.TITLE",
        icon: "link",
        authorities: [Role.MANAGE_PAYMENT_LINK, Role.PAYMENT_VIEW],
      },
      {
        menu: "payment-authz",
        path: APP_PATHS.payment.authorizations,
        label: "AUTH_DETAILS.MENU_AUTH_LIST",
        icon: "list-check",
        authorities: [Role.MANAGE_PAYMENT_AUTHZ, Role.PAYMENT_VIEW],
      },
      {
        menu: "saved-cards",
        path: APP_PATHS.payment.savedCards,
        label: "SAVED_CARDS.PAGE.TITLE",
        icon: "credit-card",
        authorities: [Role.MANAGE_SAVED_CARDS, Role.PAYMENT_VIEW],
      },
    ],
  },

  // ==================== PARAMÈTRES ====================
  {
    section: "settings",
    label: "MENU.SECTION.SETTINGS",
    icon: "settings",
    dividerBefore: true,
    // /MESSAGING added alongside /ADMINISTRATION and /ORDER — see the matching comment on the
    // "settings" route in app.routes.ts. Individual items below still gate on their own specific
    // authorities, so this only controls whether the section itself is reachable at all.
    groups: ["/ADMINISTRATION", "/ORDER", "/MESSAGING"],
    items: [
      {
        menu: "company-settings",
        path: APP_PATHS.settings.company,
        label: "MENU.ADMINISTRATION.GENERAL_PARAMS",
        icon: "sliders",
        authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
      },
      {
        menu: "company-subscription",
        path: APP_PATHS.payment.subscription,
        label: "COMPANY_BILLING.TITLE",
        icon: "credit-card",
        authorities: [Role.COMPANY_MANAGE, Role.MANAGE_SUBSCRIPTION],
      },
      {
        menu: "team",
        path: APP_PATHS.settings.agents,
        label: "MENU.SETTINGS_TEAM_PARAMS",
        icon: "users",
        authorities: [Role.MANAGE_AGENT, Role.MANAGE_AGENT_GROUP],
        items: [
          {
            path: APP_PATHS.settings.agents,
            label: "MENU.ADMINISTRATION.AGENTS",
            icon: "users",
            authorities: [Role.MANAGE_AGENT],
          },
          {
            path: APP_PATHS.settings.agentGroups,
            label: "MENU.ADMINISTRATION.GROUP_AGENT",
            icon: "users",
            //authorities: [Role.MANAGE_AGENT_GROUP],
            authorities: [Role.MANAGE_AGENT],
            requiresDisplayGroupAgent: true,
          },
        ],
      },
      {
        menu: "cms-settings",
        path: APP_PATHS.settings.catalogIntegration,
        label: "MENU.SETTINGS_CMS_GROUP",
        icon: "store",
        authorities: [Role.PRODUCT, Role.AUTOMATION_DEF_ACTIONS, Role.WORKFLOW_VIEW],
        roleMode: "any",
        items: [
          {
            // Shops, ERPs and Zoho CRM (the former Automations > Integrations).
            path: APP_PATHS.settings.catalogIntegration,
            label: "MENU.CRM.CONNEXION",
            icon: "link",
            authorities: [Role.PRODUCT, Role.AUTOMATION_DEF_ACTIONS, Role.WORKFLOW_VIEW],
            roleMode: "any",
          },
          {
            path: APP_PATHS.settings.catalogProducts,
            label: "MENU.CRM.LIST",
            icon: "store",
            authorities: [Role.PRODUCT],
          },
        ],
      },
      {
        // Every import of the company; each service only lists what the user may import.
        menu: "import-history",
        path: APP_PATHS.importHistory,
        label: "IMPORT.HISTORY.TITLE",
        icon: "history",
        authorities: [
          Role.PRODUCT,
          Role.MANAGE_CONTACT,
          Role.CONTACT_VIEW,
          Role.MANAGE_VOUCHER,
          Role.VOUCHER_VIEW,
          Role.AUDIT_MANAGE,
        ],
        roleMode: "any",
      },
      {
        menu: "audit",
        path: APP_PATHS.settings.audit,
        label: "MENU.SETTINGS_AUDIT",
        icon: "newspaper",
        authorities: [Role.AUDIT_MANAGE],
      },
      {
        menu: "notifications-settings",
        path: APP_PATHS.settings.notifications,
        label: "MENU.AUTOMATIONS.NOTIFICATION",
        icon: "bell",
        authorities: [Role.AUDIT_MANAGE],
      },
    ],
  },

  // ==================== PLATFORM ====================
  {
    section: "platform",
    label: "MENU.SECTION.PLATFORM",
    icon: "server",
    dividerBefore: true,
    groups: ["/SOLUTION"],
    items: [
      {
        menu: "companies",
        path: APP_PATHS.platform.companies,
        label: "MENU.PLATFORM_COMPANIES",
        icon: "building",
        authorities: [Role.DASHY_COMPANIES, Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "subscription-plans",
        path: APP_PATHS.platform.subscriptionPlans,
        label: "COMPANY_PLANS.TITLE",
        icon: "credit-card",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "courier-rates",
        path: APP_PATHS.platform.courierRates,
        label: "MENU.PLATFORM_COURIER_RATES",
        icon: "truck",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "admin-dashboard",
        path: APP_PATHS.platform.dashboard,
        label: "MENU.PLATFORM_DASHBOARD",
        icon: "bar-chart",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "usage",
        path: APP_PATHS.platform.usage,
        label: "MENU.PLATFORM_USAGE",
        icon: "layers",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "billing",
        path: APP_PATHS.platform.billing,
        label: "MENU.PLATFORM_BILLING",
        icon: "credit-card",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "video-tutorials",
        path: APP_PATHS.platform.tutorials,
        // One page for every in-app help content type (video tutorials, announcements, guides),
        // so the entry is named after all of them rather than the first tab.
        label: "MENU.PLATFORM_CONTENT",
        icon: "life-buoy",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
      {
        menu: "status-page",
        path: APP_PATHS.platform.status,
        label: "MENU.PLATFORM_STATUS",
        icon: "activity",
        authorities: [Role.ADMIN_SOLUTION_UI],
      },
    ],
  },
];

// Backward compatibility: flat menu list for any code that still imports MenuTree
import { MenuTreeType } from "../../types/menu-tree.type";
export const MenuTree: MenuTreeType[] = MenuSections.reduce(
  (acc: MenuTreeType[], section) => [...acc, ...section.items],
  [],
);
